import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
} from '@mui/material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import SchoolIcon from '@mui/icons-material/School';
import WomensIcon from '@mui/icons-material/Woman';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { toast } from 'react-toastify';
import api from '../services/api';

const Donate = () => {
  const [donationType, setDonationType] = useState('one-time');
  const [purpose, setPurpose] = useState('general');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    phone: '',
    pan: '',
  });

  const presetAmounts = [500, 1000, 2500, 5000, 10000, 25000];

  const handleDonationTypeChange = (event) => {
    setDonationType(event.target.value);
  };

  const handlePurposeChange = (event) => {
    setPurpose(event.target.value);
  };

  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (event) => {
    const value = event.target.value;
    setCustomAmount(value);
    setAmount('');
  };

  const handleDonorInfoChange = (field) => (event) => {
    setDonorInfo({
      ...donorInfo,
      [field]: event.target.value,
    });
  };

  // Load Razorpay script once
  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existingScript) return;

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const handleDonate = async () => {
    const donationAmount = amount || customAmount;
    
    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      toast.error('Please enter a valid donation amount');
      return;
    }

    if (!donorInfo.name || !donorInfo.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Create Razorpay order on backend
      const orderRes = await api.post('/payments/razorpay/order', {
        amount: donationAmount,
        purpose,
        name: donorInfo.name,
        email: donorInfo.email,
      });

      const { key, orderId, amount: orderAmount, currency } = orderRes.data.data;

      if (!window.Razorpay) {
        toast.error('Payment system not loaded. Please try again in a moment.');
        return;
      }

      const options = {
        key,
        amount: orderAmount,
        currency,
        name: 'GBCN Foundation',
        description: 'Donation',
        order_id: orderId,
        prefill: {
          name: donorInfo.name,
          email: donorInfo.email,
          contact: donorInfo.phone,
        },
        notes: {
          purpose,
        },
        theme: {
          color: '#f97316',
        },
        handler: function () {
          toast.success('Thank you! Your donation payment was successful.');
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Donation payment error:', error);
      toast.error(
        error.response?.data?.message ||
          'Unable to start payment. Please try again later.'
      );
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#f9fafb' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
          }}
        >
          Donate to GBCN Foundation
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Your contribution helps us create positive social impact. All donations are transparent and tracked.
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                    Donation Type
                  </FormLabel>
                  <RadioGroup
                    row
                    value={donationType}
                    onChange={handleDonationTypeChange}
                  >
                    <FormControlLabel value="one-time" control={<Radio />} label="One-time Donation" />
                    <FormControlLabel value="recurring" control={<Radio />} label="Monthly Recurring" />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                    Purpose of Donation
                  </FormLabel>
                  <RadioGroup
                    value={purpose}
                    onChange={handlePurposeChange}
                  >
                    <FormControlLabel
                      value="general"
                      control={<Radio />}
                      label="General Fund (Used where needed most)"
                    />
                    <FormControlLabel
                      value="education"
                      control={<Radio />}
                      label="Education Programs"
                    />
                    <FormControlLabel
                      value="women"
                      control={<Radio />}
                      label="Women Empowerment"
                    />
                    <FormControlLabel
                      value="youth"
                      control={<Radio />}
                      label="Youth & Student Mentorship"
                    />
                    <FormControlLabel
                      value="culture"
                      control={<Radio />}
                      label="Culture & Sanskriti"
                    />
                  </RadioGroup>
                </FormControl>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111827' }}>
                  Donation Amount
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {presetAmounts.map((amt) => (
                    <Grid item xs={6} sm={4} key={amt}>
                      <Button
                        variant={amount === amt.toString() ? 'contained' : 'outlined'}
                        fullWidth
                        onClick={() => handleAmountSelect(amt.toString())}
                        sx={{
                          py: 1.5,
                          borderColor: '#f97316',
                          color: amount === amt.toString() ? '#ffffff' : '#f97316',
                          backgroundColor: amount === amt.toString() ? '#f97316' : 'transparent',
                          '&:hover': {
                            backgroundColor: amount === amt.toString() ? '#ea580c' : 'rgba(249, 115, 22, 0.1)',
                          },
                        }}
                      >
                        ₹{amt.toLocaleString()}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                <TextField
                  fullWidth
                  label="Custom Amount (₹)"
                  type="number"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Enter custom amount"
                  sx={{ mb: 4 }}
                />

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111827' }}>
                  Donor Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name *"
                      value={donorInfo.name}
                      onChange={handleDonorInfoChange('name')}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email *"
                      type="email"
                      value={donorInfo.email}
                      onChange={handleDonorInfoChange('email')}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={donorInfo.phone}
                      onChange={handleDonorInfoChange('phone')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PAN (for tax exemption)"
                      value={donorInfo.pan}
                      onChange={handleDonorInfoChange('pan')}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, p: 3, backgroundColor: '#f0f9ff', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Note:</strong> All donations are eligible for tax exemption under Section 80G. 
                    A receipt will be automatically generated and sent to your email after successful payment.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleDonate}
                  startIcon={<VolunteerActivismIcon />}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    backgroundColor: '#f97316',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#ea580c',
                    },
                  }}
                >
                  Proceed to Payment
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111827' }}>
                  Why Donate?
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <SchoolIcon sx={{ fontSize: 32, color: '#f97316', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Support education and student mentorship programs
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <WomensIcon sx={{ fontSize: 32, color: '#f97316', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Empower women through skill development
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <PeopleIcon sx={{ fontSize: 32, color: '#f97316', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Support youth empowerment initiatives
                  </Typography>
                </Box>
                <Box>
                  <MenuBookIcon sx={{ fontSize: 32, color: '#f97316', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Preserve and promote Indian culture
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                  Payment Methods
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  We accept payments through:
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                  <li>UPI (Google Pay, PhonePe, Paytm)</li>
                  <li>Credit/Debit Cards</li>
                  <li>Net Banking</li>
                  <li>Bank Transfer</li>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Donate;

